import React, { useEffect, useState } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Swal from 'sweetalert2';
import moment from 'moment';
import { generateAxiosInstance } from '../hooks/useAuthorizedRequest';
import { useNavigate } from 'react-router-dom';
import Pagination from '../components/Pagination';
import { findIndexById, findPageNumber, deleteItem, SortIcon, searchArray } from '../components/Navigation';
import { Loader } from '../components/ModalsCommonFunctions';
import { generateCouponCodeFormValidation } from '../validation/Validation';
import { generateCouponCodeEditFormValidation } from '../validation/Validation';
import { ZodError } from 'zod';
import { ErrorMessage } from '../validation/validationError';

const GenerateCouponCode = () => {
    const authorizedAxios = generateAxiosInstance()
    const today = new Date().toISOString().split('T')[0]
    const initialFormState = {
        subscriptionMasterId: '',
        quantity: '',
        expiry: ''
    }

    const [couponData, setCouponData] = useState(initialFormState)
    const [couponList, setCouponList] = useState([])
    const [planList, setPlanList] = useState([])
    const [editId, setEditId] = useState(null)
    const [error, setError] = useState([])

    const [startIndex, setStartIndex] = useState(null);
    const [currPage, setCurrPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [displayedData, setDisplayedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [saveId, setSaveId] = useState(null);
    const [refresh, setRefresh] = useState(true);
    const [addNew, setAddNew] = useState(false);

    const [planSort, setPlanSort] = useState(null);
    const [quantitySort, setQuantitySort] = useState(null);
    const [dateSort, setDateSort] = useState(null);

    const [searchValue, setSearchValue] = useState(null);
    const searchKeys = ["code", "subscriptionMasterDetails.subscriptionName", "used_count", "quantity", "expiry"];

    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleNavigate = (route) => {
        navigate(route);
    };

    useEffect(() => {
        if (searchValue === '' && searchValue !== null) {
            setCurrPage(1);
            getCouponList();
        }
    }, [searchValue]);

    const handleEmptySearch = (e) => {
        setSearchValue(e.target.value);
    }

    const handleSearch = async () => {
        if (searchValue !== null) {
            setCouponList(await searchArray("A", getApiUrl(), searchKeys, searchValue.trim()))
        }
    }

    useEffect(() => {
        getPlanList();
    }, []);

    useEffect(() => {
        getCouponList();
    }, [couponData.subscriptionMasterId]);

    const getPlanList = async () => {
        try {
            const apiUrl = "/subscription";
            const response = await authorizedAxios.get(apiUrl);
            setPlanList(response.data.data);
        } catch (error) {
            const errorMsg = error?.data?.message || "Something went wrong!";
            console.log("Fetching error data: ", errorMsg);
        }
    }

    const getApiUrl = () => {
        let apiUrl = '/get-coupon-code?';
        if (couponData.subscriptionMasterId.length !== 0) {
            apiUrl += `subscriptionMasterId=${couponData.subscriptionMasterId}`
        }
        apiUrl = apiUrl.replace(/&$/, '');
        return apiUrl;
    }

    const getCouponList = async () => {
        try {
            const response = await authorizedAxios.get(getApiUrl());
            setCouponList(response.data.result);
        } catch (error) {
            const errorMsg = error?.data?.message || "Something went wrong!";
            console.log("Fetching error data: ", errorMsg);
        }
    }

    useEffect(() => {
        if (saveId) {
            const pageNumber = findPageNumber(couponList, saveId, itemsPerPage);
            setCurrPage(pageNumber);
        }
    }, [saveId, addNew])

    useEffect(() => {
        setTotalPages(Math.ceil(couponList.length / itemsPerPage));
        const startIndex = (currPage - 1) * itemsPerPage;
        let endIndex = startIndex + itemsPerPage;

        if (endIndex > couponList.length) {
            endIndex = couponList.length;
        }
        setStartIndex(startIndex);
        setDisplayedData(couponList.slice(startIndex, endIndex));
    }, [couponList, currPage, itemsPerPage]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCouponData((prevData) => {
            if (name === "quantity" || name === 'used_count') {
                const filteredValue = value.replace(/[^0-9]/g, '');
                return {
                    ...prevData,
                    [name]: filteredValue
                }
            } else {
                return {
                    ...prevData,
                    [name]: value.trim()
                }
            }
        })

        if (value.trim().length !== 0) {
            setError(prevErrors => prevErrors.filter(error => error.path[0] !== name));
        }
    }

    const generateCoupon = async () => {
        try {
            await generateCouponCodeFormValidation.parseAsync(couponData);
            setError([]);
            setLoading(true);
            const apiUrl = `/generate-coupon-code`;
            const response = await authorizedAxios.post(apiUrl, couponData);
            await getCouponList();
            setCouponData(prevData => {
                return {
                    ...prevData,
                    quantity: '',
                    expiry: ''
                }
            });

            setSaveId(response.data.result.id);
            setPlanSort(null);
            setQuantitySort(null);
            setDateSort(null);

            Swal.fire(
                { text: response.data.message }
            );
        } catch (error) {
            if (error instanceof ZodError) {
                setError(error.errors);
            } else {
                const errorMsg = error?.data?.message || 'Something went wrong!';
                Swal.fire({ text: errorMsg });
            }
        } finally {
            setLoading(false);
        }
    }

    const updateCoupon = async () => {
        try {
            await generateCouponCodeEditFormValidation.parseAsync(couponData);
            setError([]);
            setLoading(true);
            const apiUrl = `/edit-coupon-code?id=${editId}`;
            const response = await authorizedAxios.put(apiUrl, couponData);
            await getCouponList();
            setEditId(null);
            setCouponData(prevData => {
                return {
                    subscriptionMasterId: prevData.subscriptionMasterId,
                    expiry: '',
                    quantity: ''
                }
            });

            Swal.fire(
                { text: response.data.message }
            );
        } catch (error) {
            if (error instanceof ZodError) {
                setError(error.errors);
            } else {
                const errorMsg = error?.data?.message || 'Something went wrong!';
                Swal.fire({ text: errorMsg });
            }
        } finally {
            setLoading(false);
        }
    }

    const deleteCoupon = async (id) => {
        let confirmed = window.confirm('Are you sure? you want to delete this record.');
        if (confirmed) {
            try {
                setLoading(true);
                const apiUrl = `/delete-coupon-code?id=${id}`;
                const response = await authorizedAxios.delete(apiUrl);
                const page = deleteItem(currPage, itemsPerPage, couponList.length);
                await getCouponList();
                setCurrPage(page);

                Swal.fire(
                    { text: response.data.message }
                );
            } catch (error) {
                const errorMsg = error?.data?.message || 'Something went wrong!';
                Swal.fire(
                    { text: errorMsg }
                );
            } finally {
                setLoading(false);
            }
        }
    }

    const handleReset = () => {
        setEditId(null);
        setCouponData(initialFormState);
        setError([]);
        setPlanSort(null);
        setQuantitySort(null);
        setDateSort(null);
    }

    useEffect(() => {
        if (planSort === true || planSort === false) {
            setQuantitySort(null);
            setDateSort(null);
        }
    }, [planSort])

    useEffect(() => {
        if (quantitySort === true || quantitySort === false) {
            setPlanSort(null);
            setDateSort(null);
        }
    }, [quantitySort])

    useEffect(() => {
        if (dateSort === true || dateSort === false) {
            setPlanSort(null);
            setQuantitySort(null);
        }
    }, [dateSort])


    return (
        <>
            <Loader loading={loading} />
            <div className="wrapper d-flex flex-column min-vh-100 bg-light">
                <div id='scroll-up'></div>
                <Header />
                <div className="container-fluid">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb my-0 ms-2">
                            <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/dashboard')}>
                                <span>Dashboard</span>
                            </li>
                            <li className="breadcrumb-item active"><span>Generate Coupon Code</span></li>
                        </ol>
                    </nav>
                </div>
                <div className="container-lg">
                    <div className="row">
                        <div className="card border-info mx-auto mt-3">
                            <div className="card-content collpase show">
                                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                                    <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="fas fa-gift"></i>Generate Coupon Code</b></h4>
                                </div>
                                <div className="card-body ">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label for="subscriptionMasterId" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Subscription Plan :</label>
                                                    <select class="form-select" aria-label="Default select example" name='subscriptionMasterId' onChange={handleInputChange} value={couponData.subscriptionMasterId}>
                                                        <option selected value={''} disabled>Select Subscription Plan</option>
                                                        {
                                                            planList?.map((plan) => {
                                                                if (plan.length !== 0 && plan?.id !== 4) {
                                                                    return <option value={plan.id}>{plan.subscriptionName}</option>
                                                                }
                                                            })
                                                        }
                                                    </select>
                                                    <ErrorMessage errors={error} field="subscriptionMasterId" />
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="expiry" className="col-form-label"><span style={{ color: "red" }}>*</span>Expiry Date :</label>
                                                    <input type="date" className="form-control" id="expiry" name='expiry' value={couponData.expiry} onChange={handleInputChange} min={today} />
                                                    <ErrorMessage errors={error} field="expiry" />
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="quantity" className="col-form-label"><span style={{ color: "red" }}>*</span>Enter Quantity :</label>
                                                    <input type="text" className="form-control" id="quantity" name='quantity' value={couponData.quantity} onChange={handleInputChange} />
                                                    <ErrorMessage errors={error} field="quantity" />
                                                </div>
                                                {editId && <>
                                                    <div className="col-md-6">
                                                        <label for="code" className="col-form-label"><span style={{ color: "red" }}>*</span>Coupon Code :</label>
                                                        <input type="text" className="form-control" id="code" name='code' value={couponData.code} onChange={handleInputChange} />
                                                        <ErrorMessage errors={error} field="code" />
                                                    </div>
                                                    <div className="col-md-6">
                                                        <label for="used_count" className="col-form-label"><span style={{ color: "red" }}>*</span>Used Quantity :</label>
                                                        <input type="text" className="form-control" id="used_count" name='used_count' value={couponData.used_count} onChange={handleInputChange} />
                                                        {/* <ErrorMessage errors={error} field="used_count" /> */}
                                                    </div>
                                                </>}
                                            </div>

                                            <div className="row">
                                                <div className="col-md-12 mt-3 mb-1" style={{ textAlign: 'center' }}>
                                                    <div className="col-auto">
                                                        {!editId && <button className="btn btn-primary" type="button" style={{ marginRight: '20px' }} onClick={generateCoupon}>Generate</button>
                                                        }                                                        <button className="btn btn-outline-danger delete_icon" type="button" style={{ marginRight: '20px' }} onClick={handleReset}>Reset</button>

                                                        {
                                                            editId && (<button className="btn btn-success approve_icon" type="button" onClick={updateCoupon} disabled={!editId}>Update</button>)
                                                        }
                                                    </div>
                                                </div>

                                                <div className="d-grid gap-2 mt-2">
                                                    <div class="tab-content rounded-bottom custom_table">
                                                        <div className='search_btn_row'>
                                                            <input
                                                                className="form-control mr-sm-2"
                                                                type="Search"
                                                                placeholder="Search"
                                                                value={searchValue}
                                                                onChange={handleEmptySearch}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        handleSearch();
                                                                    }
                                                                }}
                                                            />
                                                            <button className="btn btn-outline-primary" onClick={handleSearch}><i class="fa fa-search" aria-hidden="true"></i></button>
                                                        </div>

                                                        <div class="tab-pane p-3 active preview" role="tabpanel" id="preview-1000">
                                                            <table class="table">
                                                                <thead>
                                                                    <tr>
                                                                        <th scope="col" className='sl_no'>Sr. No.</th>
                                                                        <th scope="col">Coupon Code</th>
                                                                        <th scope="col">
                                                                            Subscription Plan
                                                                            <SortIcon sortState={planSort} setSortState={setPlanSort} setData={setCouponList} setCurrPage={setCurrPage} sortingPath={"subscriptionMasterDetails.subscriptionName"} />
                                                                        </th>
                                                                        <th scope="col">
                                                                            Quantity
                                                                            <SortIcon sortState={quantitySort} setSortState={setQuantitySort} setData={setCouponList} setCurrPage={setCurrPage} sortingPath={"quantity"} />
                                                                        </th>
                                                                        <th scope='col'>Used Count</th>
                                                                        <th scope="col">
                                                                            Expiry Date
                                                                            <SortIcon sortState={dateSort} setSortState={setDateSort} setData={setCouponList} setCurrPage={setCurrPage} sortingPath={"expiry"} />
                                                                        </th>
                                                                        <th scope="col">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {
                                                                        displayedData.length > 0 ?
                                                                            displayedData.map((coupon, index) => {
                                                                                return (
                                                                                    <tr>
                                                                                        <th scope="row" className='sl_no'>{startIndex + index + 1}</th>
                                                                                        <td>{coupon?.code}</td>
                                                                                        <td>{coupon?.subscriptionMasterDetails?.subscriptionName}</td>
                                                                                        <td>{coupon.quantity}</td>
                                                                                        <td>{coupon?.used_count}</td>
                                                                                        <td>{moment(coupon.expiry).format("DD/MM/YYYY")}</td>
                                                                                        <td>
                                                                                            <button className="btn btn-outline-warning edit_icon" type="button" onClick={() => {
                                                                                                setCouponData({
                                                                                                    subscriptionMasterId: coupon.subscriptionMasterId.toString(),
                                                                                                    expiry: coupon.expiry,
                                                                                                    quantity: coupon.quantity.toString(),
                                                                                                    code: coupon.code.toString(),
                                                                                                    used_count: coupon.used_count.toString()
                                                                                                });
                                                                                                setEditId(coupon.id);
                                                                                                document.getElementById('scroll-up').scrollIntoView({ behavior: 'smooth' });
                                                                                            }}><i class="fa-light fa-pen-to-square"></i></button>
                                                                                            <button className="btn btn-outline-danger delete_icon" type="button" disabled={editId === coupon.id} onClick={() => deleteCoupon(coupon.id)}> <i class="fa-light fa-trash-can"></i></button>
                                                                                        </td>
                                                                                    </tr>
                                                                                )
                                                                            })
                                                                            :
                                                                            (<tr>
                                                                                <th colspan={"100%"} style={{ textAlign: 'center' }}>No Coupon Available</th>
                                                                            </tr>)
                                                                    }

                                                                </tbody>
                                                            </table>

                                                            <Pagination currPage={currPage} setCurrPage={setCurrPage} totalPages={totalPages} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} totalItem={couponList.length} />

                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
};

export default GenerateCouponCode;